import * as THREE from "three";
import { coverFitUVTransform, coverFitCameraVerticalFovDeg, type Pose, type UVTransform } from "@visorxr/core";

/**
 * Nominal device rear-camera horizontal FOV (degrees), used to match the render projection to the
 * camera feed on the gyro path so objects stay anchored as the user pans. Real intrinsics aren't
 * exposed by getUserMedia; this is a sane default — tune per target hardware if objects still slide.
 */
const DEFAULT_CAMERA_HFOV_DEG = 65;

export interface ARSceneOptions {
  /** Reuse an existing canvas (e.g. the WebXR base layer); otherwise the renderer creates one. */
  canvas?: HTMLCanvasElement;
  /** Override the assumed device-camera horizontal FOV (degrees) for gyro-path FOV matching. */
  cameraHFovDeg?: number;
}

/**
 * Owns the three.js scene, camera, renderer and the signage group, and applies uniform poses.
 * It does not touch `document` (SRP) — the caller mounts `renderer.domElement`.
 */
export class ARScene {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;
  private signage: THREE.Object3D | null = null;
  private backgroundTexture: THREE.VideoTexture | null = null;
  private probeKit: {
    target: THREE.WebGLRenderTarget;
    material: THREE.ShaderMaterial;
    scene: THREE.Scene;
    camera: THREE.OrthographicCamera;
    control: THREE.DataTexture;
    pixels: Uint8Array;
    /** Null until the control texture has been read once; false pins the kit broken for the session. */
    healthy: boolean | null;
  } | null = null;
  private backgroundVideo: HTMLVideoElement | null = null;
  private backgroundCoverFit: UVTransform | null = null;
  private readonly cameraHFovDeg: number;

  constructor(options: ARSceneOptions = {}) {
    this.cameraHFovDeg = options.cameraHFovDeg ?? DEFAULT_CAMERA_HFOV_DEG;
    this.renderer = new THREE.WebGLRenderer({
      canvas: options.canvas,
      alpha: true,
      antialias: true,
      // Required so a capture can read the drawing buffer back outside the render call (gyro path).
      preserveDrawingBuffer: true,
    });
    this.renderer.setPixelRatio(Math.min(globalThis.devicePixelRatio ?? 1, 2));

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(60, 1, 0.01, 100);
    this.addLights();
  }

  private addLights(): void {
    this.scene.add(new THREE.HemisphereLight(0xffffff, 0x444444, 1.2));
    const key = new THREE.DirectionalLight(0xffffff, 1.0);
    key.position.set(1, 3, 2);
    this.scene.add(key);
  }

  setSize(width: number, height: number): void {
    // A collapsed host — a hidden iframe, a container mid-animation — reports a zero dimension.
    // `width / height` would then poison the projection matrix with 0 or NaN, and nothing is
    // visible at that size anyway; the size that comes back re-runs all of this.
    if (width <= 0 || height <= 0) return;
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.applyBackgroundCoverFit(); // re-fit the feed when the viewport aspect changes
  }

  /** Camera-feed background for the self-rendered (gyro) path, cover-fitted to the viewport. */
  setBackgroundVideo(video: HTMLVideoElement): void {
    this.clearBackground();
    const texture = new THREE.VideoTexture(video);
    texture.colorSpace = THREE.SRGBColorSpace;
    this.backgroundTexture = texture;
    this.backgroundVideo = video;
    this.scene.background = texture;
    this.applyBackgroundCoverFit();
  }

  /**
   * Render `texture` onto a tiny offscreen target and hand back its RGBA pixels, or null when the
   * probe machinery itself is unavailable. The one question this answers is whether a texture's
   * *upload path* delivers pixels at all: some WebKit embeddings (WKWebView — Chrome on iOS)
   * upload a MediaStream-backed video fine while a file-backed one yields nothing, and no API
   * short of a readback can tell.
   *
   * The probe samples through a raw shader on purpose — the same unconverted `texture2D` the
   * overlay itself draws with. A built-in material would run the colour pipeline, whose
   * sRGB-to-linear decode crushes every byte below ~7 to zero and would convict dark content of
   * being an upload failure. And before the first real probe, a solid-white control texture runs
   * through the same kit once: a control that reads black means the *probe* is broken (a lost
   * context, a clipped quad), and every later call returns null rather than manufacturing
   * evidence against the video.
   */
  readTexturePixels(texture: THREE.Texture): Uint8Array | null {
    if (!this.probeKit) {
      const target = new THREE.WebGLRenderTarget(2, 2);
      const material = new THREE.ShaderMaterial({
        uniforms: { map: { value: null } },
        vertexShader: /* glsl */ `
          varying vec2 vUv;
          void main() {
            vUv = uv;
            gl_Position = vec4(position.xy, 0.0, 1.0);
          }
        `,
        fragmentShader: /* glsl */ `
          uniform sampler2D map;
          varying vec2 vUv;
          void main() {
            gl_FragColor = texture2D(map, vUv);
          }
        `,
        depthTest: false,
        depthWrite: false,
      });
      const scene = new THREE.Scene();
      scene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material));
      const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
      const control = new THREE.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1);
      control.needsUpdate = true;
      this.probeKit = { target, material, scene, camera, control, pixels: new Uint8Array(16), healthy: null };
    }
    const kit = this.probeKit;
    if (kit.healthy === null) {
      this.renderProbe(kit, kit.control);
      kit.healthy = kit.pixels.some((value) => value > 0);
    }
    if (!kit.healthy) return null;
    this.renderProbe(kit, texture);
    return kit.pixels;
  }

  private renderProbe(kit: NonNullable<typeof this.probeKit>, texture: THREE.Texture): void {
    kit.material.uniforms.map.value = texture;
    const previous = this.renderer.getRenderTarget();
    this.renderer.setRenderTarget(kit.target);
    this.renderer.render(kit.scene, kit.camera);
    this.renderer.readRenderTargetPixels(kit.target, 0, 0, 2, 2, kit.pixels);
    this.renderer.setRenderTarget(previous);
  }

  /** Drop and dispose the camera-feed background (avoids a GPU texture leak across start/stop). */
  clearBackground(): void {
    this.scene.background = null;
    this.backgroundTexture?.dispose();
    this.backgroundTexture = null;
    this.backgroundVideo = null;
    this.backgroundCoverFit = null;
  }

  /**
   * The crop currently applied to the background feed, or null before a background is set or its
   * video metadata is ready. `HomographySurface` composes this into its own frame-to-clip-space
   * mapping so a planar overlay registers against the *visible* (cropped) background rather than
   * the full camera frame.
   */
  getBackgroundCoverFit(): UVTransform | null {
    return this.backgroundCoverFit;
  }

  // Cover-fit the feed via the background texture's UV transform (three copies texture.matrix into
  // the background shader), so it fills the screen without stretching regardless of sensor aspect.
  private applyBackgroundCoverFit(): void {
    const video = this.backgroundVideo;
    const texture = this.backgroundTexture;
    const { width, height } = this.renderer.domElement;
    // Every dimension here is a divisor. A zero width cached a repeat.x of 0, which is what
    // HomographySurface then divides frame fractions by — NaN at the centre, ±Infinity elsewhere.
    // The last good crop is deliberately kept rather than cleared: it is still the one on the
    // texture, so clearing it would make the getter contradict what is actually applied.
    if (!video || !texture || !video.videoWidth || !video.videoHeight || !width || !height) return;

    const videoAspect = video.videoWidth / video.videoHeight;
    const screenAspect = width / height;

    const fit = coverFitUVTransform(videoAspect, screenAspect);
    this.backgroundCoverFit = fit;
    texture.repeat.set(fit.repeat.x, fit.repeat.y);
    texture.offset.set(fit.offset.x, fit.offset.y);

    // Match the render FOV to the (cover-fitted) feed so the 3D stays anchored as the user pans.
    // Gyro path only — this method runs solely while a background video is set; WebXR drives its
    // own projection and never enters here.
    this.camera.fov = coverFitCameraVerticalFovDeg(videoAspect, screenAspect, this.cameraHFovDeg);
    this.camera.updateProjectionMatrix();
  }

  /** Replace the signage object (e.g. a loaded glTF). */
  setSignage(object: THREE.Object3D): void {
    if (this.signage) this.scene.remove(this.signage);
    this.signage = object;
    this.scene.add(object);
  }

  /** Gyro path drives the camera from the device orientation. */
  setCameraPose(pose: Pose): void {
    this.camera.position.set(pose.position.x, pose.position.y, pose.position.z);
    this.camera.quaternion.set(pose.orientation.x, pose.orientation.y, pose.orientation.z, pose.orientation.w);
  }

  /** Both paths place the signage from the uniform object pose. */
  setObjectPose(pose: Pose): void {
    if (!this.signage) return;
    this.signage.position.set(pose.position.x, pose.position.y, pose.position.z);
    this.signage.quaternion.set(pose.orientation.x, pose.orientation.y, pose.orientation.z, pose.orientation.w);
  }

  render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  /**
   * Render the 3D with a transparent background for capture, so the 2D compositor's cover-fitted
   * camera layer is authoritative (the live background video would otherwise overwrite it).
   */
  renderForCapture(): void {
    const background = this.scene.background;
    this.scene.background = null;
    this.renderer.render(this.scene, this.camera);
    this.scene.background = background;
  }

  dispose(): void {
    this.clearBackground();
    this.probeKit?.target.dispose();
    this.probeKit?.material.dispose();
    this.probeKit?.control.dispose();
    (this.probeKit?.scene.children[0] as THREE.Mesh | undefined)?.geometry.dispose();
    this.probeKit = null;
    this.renderer.dispose();
  }
}
